import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Paper,
  Button,
  Chip,
  IconButton,
  Stack,
  TextField,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { getOnlineOrdersInTenants } from "../../../Services/ReportsApi";

const OnlineOrderTanentReports = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState(""); // <-- new

  const fetch = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await getOnlineOrdersInTenants();
      const data = res?.data || res; // tolerate different shapes
      const items = Array.isArray(data) ? data : (data?.data ?? []);
      setRows(items);
    } catch (err) {
      console.error(err);
      setError("فشل في جلب بيانات التقارير");
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetch();
  }, []);

  // فلترة محلية بناءً على نص البحث
  const normalizedSearch = String(search || "").trim().toLowerCase();
  const displayRows = normalizedSearch
    ? rows.filter((r) => {
        return (
          String(r.id).toLowerCase().includes(normalizedSearch) ||
          String(r.tenancy_db_name ?? "").toLowerCase().includes(normalizedSearch) ||
          String(r.plane_id ?? "").toLowerCase().includes(normalizedSearch) ||
          String(r.orders_count ?? "").toLowerCase().includes(normalizedSearch) ||
          String(r.start_date ?? "").toLowerCase().includes(normalizedSearch) ||
          String(r.end_date ?? "").toLowerCase().includes(normalizedSearch)
        );
      })
    : rows;

  // تصدير المصفوفة rows إلى CSV (يمكن فتحه في Excel) — تم تعديلها لدعم العربية وBOM
  const exportToCSV = (data = displayRows) => {
    if (!Array.isArray(data) || data.length === 0) {
      return;
    }
    // رؤوس الأعمدة بالعربية
    const headers = [
      "معرف النسخة",
      "معرف الباقة",
      "عدد الطلبات",
      "تاريخ البداية",
      "تاريخ الانتهاء",
      "الحالة"
    ];
    const csvRows = [];
    csvRows.push(headers.join(","));

    data.forEach((r) => {
      const statusText = Number(r.status) === 1 ? "نشط" : "غير نشط";
      const start = r.start_date ? String(r.start_date).split("T")[0] : "";
      const end = r.end_date ? String(r.end_date).split("T")[0] : "";
      const row = [
        `"${String(r.id ?? "").replace(/"/g, '""')}"`,
        `"${String(r.plane_id ?? "").replace(/"/g, '""')}"`,
        `"${String(r.orders_count ?? "").replace(/"/g, '""')}"`,
        `"${start.replace(/"/g, '""')}"`,
        `"${end.replace(/"/g, '""')}"`,
        `"${statusText.replace(/"/g, '""')}"`
      ];
      csvRows.push(row.join(","));
    });

    const csvString = csvRows.join("\n");
    // إضافة BOM لضمان فتح عربي في Excel
    const bom = "\uFEFF";
    const blob = new Blob([bom + csvString], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    const filename = `online_orders_tenants_${new Date().toISOString().slice(0,10)}.csv`;
    link.setAttribute("href", url);
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2, gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton onClick={fetch} title="تحديث">
            <RefreshIcon />
          </IconButton>
          <TextField
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="ابحث باسم النسخة، معرف الخطة، أو عدد الطلبات..."
            sx={{ minWidth: 320 }}
          />
        </Box>
        <Box>
          <Button
            variant="contained"
            color="primary"
            startIcon={<FileDownloadIcon />}
            onClick={() => exportToCSV(displayRows)} // يصدر الصفوف المعروضة حالياً (مع البحث/الفلتر) وبالعربي
            size="small"
            sx={{ textTransform: 'none' }}
          >
            تصدير إلى Excel
          </Button>
        </Box>
      </Stack>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : displayRows.length === 0 ? (
        <Alert severity="info">لا توجد بيانات حالياً</Alert>
      ) : (
        <TableContainer component={Paper} elevation={2}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>اسم النسخة</TableCell>
                <TableCell>عدد الطلبات</TableCell>
                <TableCell>تاريخ البداية</TableCell>
                <TableCell>تاريخ الانتهاء</TableCell>
                <TableCell>حالة</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {displayRows.map((r) => (
                <TableRow key={r.id}>
                  <TableCell>
                    <Typography variant="caption">{String(r.id)}</Typography>
                  </TableCell>        
                <TableCell>
                    <Chip label={r.orders_count ?? 0} color={r.orders_count > 0 ? "primary" : "default"} />
                  </TableCell>
                  <TableCell>{r.start_date ? String(r.start_date).split("T")[0] : "-"}</TableCell>
                  <TableCell>{r.end_date ? String(r.end_date).split("T")[0] : "-"}</TableCell>
                  <TableCell>
                    {Number(r.status) === 1 ? <Chip label="نشط" color="success" size="small" /> : <Chip label="غير نشط" size="small" />}
                  </TableCell>

                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default OnlineOrderTanentReports;
